import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Switch, message, Divider, Tag } from 'antd';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import type { AudioAsset, UpdateAudioRequest } from '../../types';

const { Option } = Select;
const { TextArea } = Input;

interface EditAudioModalProps {
  visible: boolean;
  audio: AudioAsset;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditAudioModal: React.FC<EditAudioModalProps> = ({
  visible,
  audio,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && audio) {
      form.setFieldsValue({
        name: audio.name,
        description: audio.description,
        category: audio.category,
        tags: audio.tags || [],
        bpm: audio.bpm,
        musicKey: audio.musicKey,
        mood: audio.mood || [],
        loopable: audio.loopable,
        intensity: audio.intensity,
        defaultVolume: audio.defaultVolume
      });
    }
  }, [visible, audio, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data: UpdateAudioRequest = {
        name: values.name,
        description: values.description,
        category: values.category,
        tags: values.tags || [],
        defaultVolume: values.defaultVolume
      };

      if (audio.type === 'music') {
        data.bpm = values.bpm;
        data.musicKey = values.musicKey;
        data.mood = values.mood || [];
        data.loopable = values.loopable;
      } else if (audio.type === 'sound_effect') {
        data.intensity = values.intensity;
      }

      await audioLibraryApi.updateAudio(audio.id, data);
      
      message.success('Audio updated successfully');
      onSuccess();
    } catch (error: any) {
      message.error('Failed to update audio: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const musicCategories = ['cinematic', 'orchestral', 'ambient', 'electronic', 'acoustic', 'other'];
  const sfxCategories = ['transition', 'impact', 'ambient', 'emotion', 'action'];
  const moodOptions = ['uplifting', 'dramatic', 'tense', 'peaceful', 'sad', 'joyful', 'mysterious', 'romantic'];
  const intensityOptions = ['soft', 'medium', 'loud'];

  return (
    <Modal
      title={`Edit Audio: ${audio.name}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={saving}
      width={600}
      okText="Save"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter audio name' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="category" label="Category">
          <Select placeholder="Select a category" allowClear>
            {(audio.type === 'music' ? musicCategories : sfxCategories).map(cat => (
              <Option key={cat} value={cat}>
                <Tag>{cat}</Tag>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="tags" label="Tags">
          <Select
            mode="tags"
            placeholder="Add tags"
            style={{ width: '100%' }}
          />
        </Form.Item>

        {audio.type === 'music' && (
          <>
            <Divider>Music Metadata</Divider>
            
            <Form.Item name="bpm" label="BPM (Beats Per Minute)">
              <InputNumber min={0} max={300} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item name="musicKey" label="Music Key">
              <Input placeholder="e.g., C Major, A Minor" />
            </Form.Item>

            <Form.Item name="mood" label="Mood">
              <Select mode="multiple" placeholder="Select mood(s)">
                {moodOptions.map(mood => (
                  <Option key={mood} value={mood}>
                    <Tag>{mood}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="loopable" label="Loopable" valuePropName="checked">
              <Switch />
            </Form.Item>
          </>
        )}

        {audio.type === 'sound_effect' && (
          <>
            <Divider>Sound Effect Metadata</Divider>
            
            <Form.Item name="intensity" label="Intensity">
              <Select placeholder="Select intensity" allowClear>
                {intensityOptions.map(intensity => (
                  <Option key={intensity} value={intensity}>
                    <Tag>{intensity}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        <Divider>Playback Settings</Divider>

        <Form.Item
          name="defaultVolume"
          label="Default Volume"
          rules={[{ required: true }]}
        >
          <InputNumber
            min={0}
            max={1}
            step={0.05}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditAudioModal;
